"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface GameEntryContextType {
  shouldShowWelcomeLoader: boolean;
  triggerWelcomeLoader: () => void;
  hideWelcomeLoader: () => void;
  isFirstGameEntry: boolean;
}

const GameEntryContext = createContext<GameEntryContextType | undefined>(undefined);

const GAME_ENTRY_KEY = 'fantaxi_game_entry_shown';

export function GameEntryProvider({ children }: { children: ReactNode }) {
  const [shouldShowWelcomeLoader, setShouldShowWelcomeLoader] = useState(false);
  const [isFirstGameEntry, setIsFirstGameEntry] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Check if user has seen the welcome loader before
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsHydrated(true);
      const hasSeenWelcome = localStorage.getItem(GAME_ENTRY_KEY);
      setIsFirstGameEntry(!hasSeenWelcome);
    }
  }, []);

  // Trigger welcome loader (called after successful login/register)
  const triggerWelcomeLoader = () => {
    if (!isHydrated) return;
    
    const hasSeenWelcome = localStorage.getItem(GAME_ENTRY_KEY);
    
    // Only show if user hasn't seen it before
    if (!hasSeenWelcome) {
      console.log('GameEntry: Triggering welcome loader for first-time user');
      setShouldShowWelcomeLoader(true);
      setIsFirstGameEntry(true);
    }
  };

  // Hide welcome loader and mark as seen
  const hideWelcomeLoader = () => {
    if (!isHydrated) return;
    
    console.log('GameEntry: Hiding welcome loader and marking as seen');
    setShouldShowWelcomeLoader(false);
    
    // Mark as seen in localStorage
    localStorage.setItem(GAME_ENTRY_KEY, 'true');
    setIsFirstGameEntry(false);
  };

  return (
    <GameEntryContext.Provider value={{
      shouldShowWelcomeLoader,
      triggerWelcomeLoader,
      hideWelcomeLoader,
      isFirstGameEntry
    }}>
      {children}
    </GameEntryContext.Provider>
  );
}

export function useGameEntry() {
  const context = useContext(GameEntryContext);
  if (context === undefined) {
    throw new Error('useGameEntry must be used within a GameEntryProvider');
  }
  return context;
}